import React, { useState, useEffect, useMemo } from 'react';
import { getRelevantAndAllJobs, applyForTask, getMyApplications } from '../../api/taskAPI';
import { getWorkerProfile as fetchWorkerProfile } from '../../api/workerAPI';
import { useAuth } from '../../context/AuthContext';
import { Search, MapPin, Filter, CheckCircle, Briefcase, X } from 'lucide-react';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'home-based', label: 'Home-Based' },
  { value: 'part-time', label: 'Part-Time' },
  { value: 'freelance', label: 'Freelancing' },
  { value: 'local-service', label: 'Local Services' },
];

const INITIAL_APPLY_FORM = {
  message: '', availabilityDate: '', availabilityHours: '',
  expectedRateAmount: '', expectedRateType: 'fixed', expectedRateCurrency: 'INR', portfolioLink: '',
};

const Worker = () => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [relevantJobs, setRelevantJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyForm, setApplyForm] = useState(INITIAL_APPLY_FORM);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [applySuccess, setApplySuccess] = useState('');
  const [applicationToast, setApplicationToast] = useState('');
  const [applications, setApplications] = useState([]);
  const [applicationsVersion, setApplicationsVersion] = useState(0);

  useEffect(() => {
    const fetchJobs = async () => {
      if (!user || user?.role !== 'worker') return;
      setLoadingJobs(true); setJobsError('');
      try {
        const res = await getRelevantAndAllJobs();
        const mapJob = (t) => ({
          id: t._id, title: t.title, category: t.category || 'other', description: t.description,
          location: t.location?.isRemote ? 'Remote' : [t.location?.city, t.location?.state].filter(Boolean).join(', ') || 'N/A',
          payment: `₹${t.budget?.amount ?? 0} (${t.budget?.type ?? 'fixed'})`,
          duration: `${t.duration?.value ?? ''} ${t.duration?.unit ?? ''}`.trim(),
          skills: t.skillsRequired || [], provider: t.postedBy?.name || 'Unknown',
          rating: t.postedBy?.ratings?.average ?? 0, verified: t.status === 'open',
          budget: { amount: t.budget?.amount ?? null, type: t.budget?.type || 'fixed', currency: t.budget?.currency || 'INR' },
        });
        setRelevantJobs((res.relevantJobs || []).map(mapJob));
        setAllJobs((res.allJobs || []).map(mapJob));
      } catch (err) {
        setJobsError('Failed to load tasks. Please try again.');
      } finally { setLoadingJobs(false); }
    };
    fetchJobs();
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== 'worker') { setApplications([]); return; }
    const fetchProfile = async () => { try { await fetchWorkerProfile(); } catch {} };
    fetchProfile();
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== 'worker') { setApplications([]); return; }
    let ignore = false;
    const fetchApplications = async () => {
      try { const res = await getMyApplications(); if (!ignore) setApplications(res.data || []); }
      catch { if (!ignore) setApplications([]); }
    };
    fetchApplications();
    return () => { ignore = true; };
  }, [user, applicationsVersion]);

  useEffect(() => {
    if (!applicationToast) return;
    const t = setTimeout(() => setApplicationToast(''), 4000);
    return () => clearTimeout(t);
  }, [applicationToast]);

  useEffect(() => {
    if (!applySuccess) return;
    const t = setTimeout(() => setApplySuccess(''), 5000);
    return () => clearTimeout(t);
  }, [applySuccess]);

  const filteredRelevantJobs = useMemo(() => relevantJobs.filter(job => {
    const matchCategory = selectedCategory === 'all' || job.category === selectedCategory;
    const matchSearch = !searchTerm || job.title.toLowerCase().includes(searchTerm.toLowerCase()) || job.skills.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchLocation = !locationFilter || job.location.toLowerCase().includes(locationFilter.toLowerCase());
    return matchCategory && matchSearch && matchLocation;
  }), [relevantJobs, selectedCategory, searchTerm, locationFilter]);

  const filteredAllJobs = useMemo(() => allJobs.filter(job => {
    const matchCategory = selectedCategory === 'all' || job.category === selectedCategory;
    const matchSearch = !searchTerm || job.title.toLowerCase().includes(searchTerm.toLowerCase()) || job.skills.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchLocation = !locationFilter || job.location.toLowerCase().includes(locationFilter.toLowerCase());
    return matchCategory && matchSearch && matchLocation;
  }), [allJobs, selectedCategory, searchTerm, locationFilter]);

  const isWorker = user?.role === 'worker';

  const appliedTaskIds = useMemo(() => {
    if (!Array.isArray(applications)) return new Set();
    return new Set(applications.map((item) => item?.task?._id).filter(Boolean));
  }, [applications]);

  const handleOpenApply = (job, isEligible) => {
    if (!job || !isWorker) return;
    if (!isEligible) { setApplicationToast('Add required skills to your profile before applying.'); return; }
    setSelectedJob(job);
    setApplyForm({ ...INITIAL_APPLY_FORM, expectedRateAmount: job.budget?.amount ?? '', expectedRateType: job.budget?.type || 'fixed', expectedRateCurrency: job.budget?.currency || 'INR' });
    setApplyError(''); setApplySuccess(''); setShowApplyModal(true);
  };

  const handleCloseApply = () => { setShowApplyModal(false); setSelectedJob(null); setApplyError(''); setApplyForm(INITIAL_APPLY_FORM); setApplying(false); };

  const handleApplyInputChange = (field, value) => setApplyForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmitApplication = async (event) => {
    if (event) event.preventDefault();
    if (!selectedJob) return;
    setApplying(true); setApplyError('');
    try {
      await applyForTask(selectedJob.id, { ...applyForm, expectedRateAmount: applyForm.expectedRateAmount === '' ? undefined : Number(applyForm.expectedRateAmount) });
      setApplySuccess(`Application sent for ${selectedJob.title}.`);
      setApplicationToast(`Sent your application to ${selectedJob.provider || selectedJob.title}.`);
      setApplicationsVersion((prev) => prev + 1);
      handleCloseApply();
    } catch (err) {
      setApplyError(err.response?.data?.message || 'Failed to submit. Please try again.');
    } finally { setApplying(false); }
  };

  const JobCard = ({ job, eligible, applied }) => (
    <div className="rounded-2xl border p-6 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 glass-panel"
      style={{ 
        background: 'rgba(255,255,255,0.08)', 
        backdropFilter: 'blur(16px)', 
        WebkitBackdropFilter: 'blur(16px)',
        borderColor: 'rgba(255,255,255,0.15)'
      }}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>{job.title}</h3>
            {job.verified && <span className="badge-active text-xs">Open</span>}
          </div>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{job.provider} · {job.location}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold text-sm" style={{ color: 'var(--foreground)' }}>{job.payment}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{job.duration}</p>
        </div>
      </div>
      {job.description && <p className="text-sm leading-relaxed mb-4 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>{job.description}</p>}
      {job.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {job.skills.slice(0, 4).map((s) => <span key={s} className="badge-outline text-xs">{s}</span>)}
          {job.skills.length > 4 && <span className="badge-outline text-xs">+{job.skills.length - 4}</span>}
        </div>
      )}
      <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
        <p className="text-xs" style={{ color: eligible ? '#16a34a' : 'var(--muted-foreground)' }}>
          {applied ? '✓ Already applied' : eligible ? 'You match this job!' : 'Update skills to apply'}
        </p>
        {isWorker ? (
          <button type="button" onClick={() => handleOpenApply(job, eligible)} disabled={applied || !eligible}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${applied ? 'cursor-not-allowed' : eligible ? 'hover:opacity-90' : 'cursor-not-allowed opacity-40'}`}
            style={{ background: applied ? 'var(--muted)' : eligible ? 'var(--primary)' : 'var(--muted)', color: applied ? 'var(--muted-foreground)' : eligible ? 'var(--primary-foreground)' : 'var(--muted-foreground)' }}>
            {applied ? 'Applied' : eligible ? 'Apply Now' : 'Not Eligible'}
          </button>
        ) : (
          <a href="/login" className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>Sign in</a>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 text-white" 
      style={{ 
        backgroundImage: "url('/images/team/find.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        '--foreground': '#ffffff',
        '--muted-foreground': '#f4f4f5',
        '--card-foreground': '#ffffff',
        '--border': 'rgba(255, 255, 255, 0.15)',
        '--accent': 'rgba(255, 255, 255, 0.08)',
        '--muted': 'rgba(255, 255, 255, 0.05)',
      }}>

      {/* Page Header */}
      <div className="border-b glass-panel" 
        style={{ 
          background: 'rgba(255,255,255,0.08)', 
          backdropFilter: 'blur(16px)', 
          WebkitBackdropFilter: 'blur(16px)',
          borderColor: 'rgba(255,255,255,0.15)'
        }}>
        <div className="section-container py-8">
          <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Find Work</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Browse jobs matched to your skills and location</p>
        </div>
      </div>

      <div className="section-container py-8">
        <div className="grid lg:grid-cols-4 gap-8">

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="rounded-2xl border p-5 glass-panel" 
              style={{ 
                background: 'rgba(255,255,255,0.08)', 
                backdropFilter: 'blur(16px)', 
                WebkitBackdropFilter: 'blur(16px)',
                borderColor: 'rgba(255,255,255,0.15)'
              }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Search</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                <input type="text" placeholder="Job title or skill..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-9" style={{ background: 'transparent' }} />
              </div>
            </div>

            <div className="rounded-2xl border p-5 glass-panel" 
              style={{ 
                background: 'rgba(255,255,255,0.08)', 
                backdropFilter: 'blur(16px)', 
                WebkitBackdropFilter: 'blur(16px)',
                borderColor: 'rgba(255,255,255,0.15)'
              }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Location</p>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                <input type="text" placeholder="City or Remote" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
                  className="input-field pl-9" style={{ background: 'transparent' }} />
              </div>
            </div>

            <div className="rounded-2xl border p-5 glass-panel" 
              style={{ 
                background: 'rgba(255,255,255,0.08)', 
                backdropFilter: 'blur(16px)', 
                WebkitBackdropFilter: 'blur(16px)',
                borderColor: 'rgba(255,255,255,0.15)'
              }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
                <Filter className="inline w-3.5 h-3.5 mr-1.5" />Category
              </p>
              <div className="space-y-1">
                {CATEGORIES.map(({ value, label }) => (
                  <button key={value} onClick={() => setSelectedCategory(value)}
                    className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
                    style={{
                      background: selectedCategory === value ? 'var(--primary)' : 'transparent',
                      color: selectedCategory === value ? 'var(--primary-foreground)' : 'var(--foreground)',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-8">
            {/* Apply Modal */}
            {showApplyModal && selectedJob && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 backdrop-blur-sm">
                <div className="w-full max-w-lg rounded-2xl border shadow-xl overflow-hidden glass-panel" 
                  style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    backdropFilter: 'blur(16px)', 
                    WebkitBackdropFilter: 'blur(16px)',
                    borderColor: 'rgba(255,255,255,0.15)'
                  }}>
                  <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                    <div>
                      <h3 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Apply for {selectedJob.title}</h3>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{selectedJob.provider} · {selectedJob.location}</p>
                    </div>
                    <button type="button" onClick={handleCloseApply} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(255,255,255,0.1)] transition-colors">
                      <X className="w-4 h-4" style={{ color: 'var(--foreground)' }} />
                    </button>
                  </div>
                  <form onSubmit={handleSubmitApplication} className="p-6 space-y-4">
                    <div>
                      <label className="label-text">Message <span style={{ color: 'var(--destructive)' }}>*</span></label>
                      <textarea value={applyForm.message} onChange={(e) => handleApplyInputChange('message', e.target.value)}
                        rows={3} required placeholder="Why are you a great fit for this role?" className="input-field resize-none" style={{ background: 'transparent' }} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label-text">Start Date</label>
                        <input type="date" value={applyForm.availabilityDate} onChange={(e) => handleApplyInputChange('availabilityDate', e.target.value)} className="input-field" style={{ background: 'transparent' }} />
                      </div>
                      <div>
                        <label className="label-text">Hours/week</label>
                        <input type="text" value={applyForm.availabilityHours} onChange={(e) => handleApplyInputChange('availabilityHours', e.target.value)} placeholder="e.g. 20" className="input-field" style={{ background: 'transparent' }} />
                      </div>
                    </div>
                    <div>
                      <label className="label-text">Expected Rate</label>
                      <div className="flex gap-2">
                        <input type="number" min="0" step="0.5" value={applyForm.expectedRateAmount} onChange={(e) => handleApplyInputChange('expectedRateAmount', e.target.value)} placeholder="Amount" className="input-field" style={{ background: 'transparent' }} />
                        <select value={applyForm.expectedRateType} onChange={(e) => handleApplyInputChange('expectedRateType', e.target.value)} className="input-field w-32" style={{ background: 'transparent' }}>
                          <option value="fixed">Fixed</option>
                          <option value="hourly">Hourly</option>
                          <option value="monthly">Monthly</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label-text">Portfolio Link</label>
                      <input type="url" value={applyForm.portfolioLink} onChange={(e) => handleApplyInputChange('portfolioLink', e.target.value)} placeholder="https://..." className="input-field" style={{ background: 'transparent' }} />
                    </div>
                    {applyError && <p className="text-xs px-3 py-2 rounded-xl border" style={{ color: 'var(--destructive)', background: '#fef2f2', borderColor: '#fecaca' }}>{applyError}</p>}
                    <div className="flex justify-end gap-3 pt-2">
                      <button type="button" onClick={handleCloseApply} className="btn-outline text-sm">Cancel</button>
                      <button type="submit" disabled={applying} className="btn-primary text-sm">{applying ? 'Submitting...' : 'Send Application'}</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Toast */}
            {applicationToast && (
              <div className="fixed bottom-6 right-6 z-40 rounded-xl border px-4 py-3 shadow-lg flex items-center gap-3 glass-panel"
                style={{ 
                  background: 'rgba(255,255,255,0.08)', 
                  backdropFilter: 'blur(16px)', 
                  WebkitBackdropFilter: 'blur(16px)',
                  borderColor: 'rgba(255,255,255,0.15)'
                }}>
                <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#16a34a' }} />
                <span className="text-sm" style={{ color: 'var(--foreground)' }}>{applicationToast}</span>
                <button onClick={() => setApplicationToast('')}><X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} /></button>
              </div>
            )}

            {/* Relevant Jobs */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>Matched for You</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                    {!loadingJobs && `${filteredRelevantJobs.length} job${filteredRelevantJobs.length !== 1 ? 's' : ''} matching your skills`}
                  </p>
                </div>
                <button onClick={() => window.location.reload()} className="btn-outline text-xs !py-1.5 !px-3">Refresh</button>
              </div>

              {loadingJobs && <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.15)', borderTopColor: 'var(--foreground)' }} /></div>}
              {jobsError && !loadingJobs && (
                <div className="rounded-2xl border p-8 text-center glass-panel" 
                  style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    backdropFilter: 'blur(16px)', 
                    WebkitBackdropFilter: 'blur(16px)',
                    borderColor: 'rgba(255,255,255,0.15)'
                  }}>
                  <p className="text-sm mb-3" style={{ color: 'var(--destructive)' }}>{jobsError}</p>
                  <button onClick={() => window.location.reload()} className="btn-outline text-xs">Retry</button>
                </div>
              )}
              {!loadingJobs && !jobsError && filteredRelevantJobs.length === 0 && (
                <div className="rounded-2xl border p-10 text-center glass-panel" 
                  style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    backdropFilter: 'blur(16px)', 
                    WebkitBackdropFilter: 'blur(16px)',
                    borderColor: 'rgba(255,255,255,0.15)'
                  }}>
                  <Briefcase className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                  <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>No matched jobs yet</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Add more skills to your profile to see matched listings.</p>
                </div>
              )}
              <div className="space-y-4">
                {filteredRelevantJobs.map((job) => (
                  <JobCard key={job.id} job={job} eligible={true} applied={appliedTaskIds.has(job.id)} />
                ))}
              </div>
            </div>

            {/* All Jobs */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>All Jobs</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{!loadingJobs && `${filteredAllJobs.length} total`}</p>
                </div>
              </div>
              {!loadingJobs && !jobsError && filteredAllJobs.length === 0 && (
                <div className="rounded-2xl border p-10 text-center glass-panel" 
                  style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    backdropFilter: 'blur(16px)', 
                    WebkitBackdropFilter: 'blur(16px)',
                    borderColor: 'rgba(255,255,255,0.15)'
                  }}>
                  <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>No other jobs available</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Check back later for more opportunities.</p>
                </div>
              )}
              <div className="space-y-4">
                {filteredAllJobs.map((job) => (
                  <JobCard key={job.id} job={job} eligible={false} applied={appliedTaskIds.has(job.id)} />
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Worker;
